import apiFetch from "@wordpress/api-fetch";
import {
	GlobalColorStyle,
	GlobalStyles,
	GlobalTypographyStyle,
} from "../types";
import { debounceAsync, deepClone, isEqual, localized, _set } from "../utils";
import { get, registerMiddleware, useSharedState } from "./useSharedState";

registerMiddleware("global-styles", (value, next) => {
	next(value);
	new GlobalStylesProcessor(value).process();
	if (isEqual(value, localized.configs["global-styles"])) return;
	const debouncedApiFetch = debounceAsync(apiFetch, 2500);
	debouncedApiFetch({
		path: "magazine-blocks/v1/settings",
		method: "POST",
		data: {
			"global-styles": JSON.stringify({
				...value,
				_modified: new Date().toISOString(),
			}),
		},
	}).catch(() => {});
});

registerMiddleware("magazine-blocks-device", (value, next) => {
	next(value);

	setTimeout(() => {
		const _window = window["editor-canvas"];

		if (typeof _window !== "undefined") {
			new GlobalStylesProcessor(get("global-styles")).process();
		}
	}, 700);
});

export const DEFAULT_IDS = ["primary", "secondary", "text", "accent"];

let counter = {
	colors: localized.configs["global-styles"].colors.filter(
		(color) => !DEFAULT_IDS.includes(color.id)
	).length,
	typographies: localized.configs["global-styles"].typographies.filter(
		(typography) => !DEFAULT_IDS.includes(typography.id)
	).length,
};

type Style = GlobalColorStyle | GlobalTypographyStyle;

const useGlobalStyles = () => {
	const [styles, setStyles] = useSharedState<GlobalStyles>(
		"global-styles",
		localized.configs["global-styles"]
	);

	const generateIdName = (key: "typographies" | "colors") => {
		counter[key]++;
		const baseName =
			key === "typographies" ? "custom-typography" : "custom-color";
		let id = `${baseName}-${counter[key]}`;
		let name = baseName.replaceAll("-", " ");
		// @ts-ignore
		while ((styles?.[key] || []).find((n) => n.id === id)) {
			counter[key]++;
			id = `${baseName}-${counter[key]}` as string;
		}
		return {
			id,
			name: `${name}#${counter[key]}`,
		};
	};

	const updateStyleByKey = (
		key: "colors" | "typographies",
		newStyle: Style
	) => {
		let nextStyles = deepClone(styles);
		let index = (nextStyles?.[key] || []).findIndex(
			(n) => n.id === newStyle.id
		);
		if (-1 !== index) {
			nextStyles = _set(nextStyles, [key, index], newStyle);
		} else {
			const idName = generateIdName(key);
			// @ts-ignore
			nextStyles = _set(
				nextStyles,
				[key, nextStyles?.[key].length ?? 0],
				{
					...newStyle,
					...idName,
				}
			);
		}
		setStyles(nextStyles as GlobalStyles);
		return nextStyles;
	};

	const removeStyleByKey = (key: "colors" | "typographies", id: string) => {
		const nextStyles = deepClone(styles);
		const removed = styles?.[key]?.findIndex((v) => v.id === id);
		if (typeof removed === "undefined" || -1 === removed) return styles;
		nextStyles?.[key].splice(removed, 1);
		setStyles(nextStyles as GlobalStyles);
		return nextStyles;
	};

	return {
		styles: styles as GlobalStyles,
		setStyles,
		updateStyleByKey,
		removeStyleByKey,
	} as const;
};

export class GlobalStylesProcessor {
	private _styles: GlobalStyles;
	private _colors: Array<string>;
	private _typographies: Array<string>;
	private _typographiesTablet: Array<string>;
	private _typographiesMobile: Array<string>;
	private _fonts: Record<string, Array<string | number>>;
	private _editorCanvas: Document;
	private _css: string;

	constructor(styles: GlobalStyles) {
		this._styles = styles;
		this._colors = [];
		this._typographies = [];
		this._typographiesTablet = [];
		this._typographiesMobile = [];
		this._fonts = {};
		this._css = "";
		this._editorCanvas = window["editor-canvas"]?.document;
	}

	process() {
		this._processColors();
		this._processTypographies();
		this._makeStyle();
		if (this._editorCanvas) {
			this._makeStyle(this._editorCanvas);
		}
	}

	private _processColors() {
		if (!this._styles.colors.length) return;
		for (const color of this._styles.colors) {
			if (!color.value) continue;
			this._colors.push(`--mzb-colors-${color.id}: ${color.value};`);
		}
	}

	private _processTypographies() {
		if (!this._styles.typographies.length) return;
		for (const typography of this._styles.typographies) {
			if (!typography.value) continue;
			let selectorCSS = "";
			const {
				family,
				weight = 400,
				size,
				lineHeight,
				transform,
				letterSpacing,
				fontStyle,
				decoration,
			} = typography.value;
			if (family && "Default" !== family) {
				if (!this._fonts[family]) {
					this._fonts[family] = [];
				}
				if (!this._fonts[family].includes(weight)) {
					this._fonts[family].push(weight);
				}
				this._typographies.push(
					`--mzb-font-families-${typography.id}: ${family};`
				);
				selectorCSS += `font-family: var(--mzb-font-families-${typography.id}) !important;`;
			}
			if (weight) {
				selectorCSS += `font-weight: var(--mzb-font-weights-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-weights-${typography.id}: ${weight};`
				);
			}
			if (transform && "default" !== transform) {
				selectorCSS += `text-transform: var(--mzb-font-transforms-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-transforms-${typography.id}: ${transform};`
				);
			}
			if (decoration && "default" !== decoration) {
				selectorCSS += `text-decoration: var(--mzb-font-decorations-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-decorations-${typography.id}: ${decoration};`
				);
			}
			if (fontStyle && "default" !== fontStyle) {
				this._typographies.push(
					`--mzb-font-styles-${typography.id}: ${fontStyle};`
				);
				selectorCSS += `font-style: var(--mzb-font-styles-${typography.id}) !important;`;
			}
			if (size) {
				this._processResponsiveTypography(
					size,
					`--mzb-font-sizes-${typography.id}`
				);
				selectorCSS += `font-size: var(--mzb-font-sizes-${typography.id}) !important;`;
			}
			if (letterSpacing) {
				this._processResponsiveTypography(
					letterSpacing,
					`--mzb-font-letter-spacings-${typography.id}`
				);
				selectorCSS += `letter-spacing: var(--mzb-font-letter-spacings-${typography.id}) !important;`;
			}
			if (lineHeight) {
				this._processResponsiveTypography(
					lineHeight,
					`--mzb-font-line-heights-${typography.id}`
				);
				selectorCSS += `line-height: var(--mzb-font-line-heights-${typography.id}) !important;`;
			}
			if (selectorCSS) {
				this._css =
					this._css +
					`.mzb-typography-${typography.id} {${selectorCSS}}`;
			}
		}
	}

	private _processResponsiveTypography(
		values: Record<string, { value: number; unit?: string }>,
		cssPropName: string
	) {
		if (values?.desktop?.value) {
			this._typographies.push(
				`${cssPropName}: ${values.desktop.value}${
					values.desktop?.unit ?? "px"
				};`
			);
		}
		if (values?.tablet?.value) {
			this._typographiesTablet.push(
				`${cssPropName}: ${values.tablet.value}${
					values.tablet?.unit ?? "px"
				};`
			);
		}
		if (values?.mobile?.value) {
			this._typographiesMobile.push(
				`${cssPropName}: ${values.mobile.value}${
					values.mobile?.unit ?? "px"
				};`
			);
		}
	}

	private _makeStyle(_doc = document) {
		const styleEl = _doc.querySelector(
			"#magazine-blocks-global-styles-css"
		);

		let css = `:root {${this._colors
			.concat(this._typographies)
			.join("\n")}}`;

		if (this._typographiesTablet) {
			css += `\n@media (max-width: 779px) { :root { ${this._typographiesTablet.join(
				"\n"
			)} } }`;
		}

		if (this._typographiesMobile) {
			css += `\n@media (max-width: 359px) { :root { ${this._typographiesMobile.join(
				"\n"
			)} } }`;
		}
		if (this._css) {
			css += `\n${this._css}`;
		}

		if (this._fonts) {
			css =
				Object.entries(this._fonts)
					.map(([fontName, fontWeights]) => {
						const weightsString = fontWeights.join(",");
						return `@import url('https://fonts.googleapis.com/css2?family=${fontName.replace(
							/\s/g,
							"+"
						)}:wght@${weightsString}&display=swap');`;
					})
					.join("\n") +
				"\n" +
				css;
		}

		if (styleEl) {
			styleEl.innerHTML = css;
			return;
		}

		_doc.head.appendChild(
			Object.assign(_doc.createElement("style"), {
				id: "magazine-blocks-global-styles-css",
				innerHTML: css,
			})
		);
	}
}

export default useGlobalStyles;
